
import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

type Testimonial = {
  text: string;
  author: string;
  role: string;
};

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
  autoSlideInterval?: number;
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({
  testimonials,
  autoSlideInterval = 5000,
}) => {
  const [api, setApi] = useState<any>();
  const [current, setCurrent] = useState(0);
  const count = testimonials.length;

  useEffect(() => {
    if (!api) return;
    
    setCurrent(api.selectedScrollSnap());
    
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (api) {
        api.scrollNext();
      }
    }, autoSlideInterval);
    
    return () => clearInterval(timer);
  }, [api, autoSlideInterval]);

  return (
    <Carousel
      setApi={setApi}
      className="w-full max-w-5xl mx-auto py-8"
      opts={{
        align: "start",
        loop: true,
      }}
    >
      <CarouselContent>
        {testimonials.map((testimonial, index) => (
          <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
            <div className="p-1">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full bg-white rounded-2xl overflow-hidden">
                <CardContent className="p-6 flex flex-col h-full relative">
                  <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-toctoc-orange to-toctoc-blue"></div>
                  <div className="pl-3">
                    <div className="mb-6 flex">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="text-toctoc-orange text-lg">★</span>
                      ))}
                    </div>
                    <p className="italic mb-6 text-toctoc-gray-dark flex-grow">{testimonial.text}</p>
                    <div className="border-t pt-4 border-gray-100 mt-auto">
                      <p className="font-semibold text-toctoc-black">{testimonial.author}</p>
                      <p className="text-sm text-toctoc-gray-medium">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>

      <div className="flex justify-center gap-2 pt-6">
        {Array.from({ length: count }).map((_, index) => (
          <button
            key={index}
            className={`h-2 rounded-full transition-all ${
              current === index ? "w-6 bg-toctoc-orange" : "w-2 bg-gray-200"
            }`}
            onClick={() => api?.scrollTo(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </Carousel>
  );
};

export default TestimonialCarousel;
